import { useState, useEffect } from 'react';
import type {
  WiseguyAlertFilters,
  WiseguyFilterPreset,
  ContractType,
  Bias,
  AssetClass,
  BasketTag,
} from '../types/wiseguy';
import { TickerTagInput } from './TickerTagInput';

interface WiseguyFilterPresetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (preset: WiseguyFilterPreset) => void;
  preset?: WiseguyFilterPreset | null; // null = new preset
}

const defaultFilters: WiseguyAlertFilters = {
  symbol: '',
  bias: [],
  contract_type: '',
  asset_class: [],
  top_bet: '',
  bet_min: '',
  bet_max: '',
  multiplier_min: '',
  multiplier_max: '',
  expiry_min: '',
  expiry_max: '',
  received_on_min: '',
  received_on_max: '',
  sweeps_only: false,
  opening_only: false,
  dte_min: '',
  dte_max: '',
  baskets: [],
};

// Sensible defaults for new presets (sweeps + min premium)
const newPresetDefaults: WiseguyAlertFilters = {
  ...defaultFilters,
  sweeps_only: true,
  bet_min: 10000,
};

// Reusable chip-toggle multiselect component
function ChipGroup<T extends string>({
  label,
  options,
  selected,
  onChange,
  colorMap,
}: {
  label: string;
  options: { value: T; label: string }[];
  selected: T[];
  onChange: (values: T[]) => void;
  colorMap?: Record<T, string>;
}): React.ReactElement {
  const toggle = (value: T) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <div>
      <label className="block text-xs text-gray-500 mb-1.5">{label}</label>
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => {
          const isActive = selected.includes(opt.value);
          const activeColor =
            colorMap?.[opt.value] ||
            'bg-accent-yellow text-hot-tag-text border-accent-yellow font-semibold';
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => toggle(opt.value)}
              className={`px-2.5 py-1 text-xs rounded border transition-colors ${
                isActive
                  ? activeColor
                  : 'bg-dark-bg text-gray-400 border-dark-border hover:border-gray-500'
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const BIAS_OPTIONS: { value: Bias; label: string }[] = [
  { value: 'Bullish', label: 'Bullish' },
  { value: 'Bearish', label: 'Bearish' },
  { value: 'Neutral', label: 'Neutral' },
];

const BIAS_COLORS: Record<Bias, string> = {
  Bullish: 'bg-bull-blue/20 text-bull-blue border-bull-blue/30',
  Bearish: 'bg-bear-red/20 text-bear-red border-bear-red/30',
  Neutral: 'bg-accent-yellow/20 text-accent-yellow border-accent-yellow/30',
};

const ASSET_CLASS_OPTIONS: { value: AssetClass; label: string }[] = [
  { value: 'Stock', label: 'Stock' },
  { value: 'ETF', label: 'ETF' },
  { value: 'Index', label: 'Index' },
];

const BASKET_OPTIONS: { value: BasketTag; label: string }[] = [
  { value: 'FANG', label: 'FANG' },
  { value: 'LEADERS', label: 'LEADERS' },
  { value: 'OI', label: 'OI' },
  { value: 'ETFS', label: 'ETFS' },
];

export function WiseguyFilterPresetModal({
  isOpen,
  onClose,
  onSave,
  preset,
}: WiseguyFilterPresetModalProps) {
  const [name, setName] = useState('');
  const [filters, setFilters] = useState<WiseguyAlertFilters>(defaultFilters);

  // Reset form when modal opens/preset changes
  useEffect(() => {
    if (isOpen) {
      if (preset) {
        setName(preset.name);
        setFilters({ ...defaultFilters, ...preset.filters });
      } else {
        // New preset: start with sweeps + $10k min to help users
        setName('');
        setFilters(newPresetDefaults);
      }
    }
  }, [isOpen, preset]);

  if (!isOpen) return null;

  const updateFilter = <K extends keyof WiseguyAlertFilters>(
    key: K,
    value: WiseguyAlertFilters[K]
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    if (!name.trim()) return;

    const newPreset: WiseguyFilterPreset = {
      id: preset?.id || `preset-${Date.now()}`,
      name: name.trim(),
      isDefault: preset?.isDefault || false,
      filters,
    };

    onSave(newPreset);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-dark-surface border border-dark-border rounded-lg w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-dark-border">
          <h2 className="text-sm font-bold text-white uppercase tracking-[1.5px]">
            {preset ? 'Edit Filter Preset' : 'New Filter Preset'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-300 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Row 1: Preset Name + Symbol */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Preset Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Large Bullish Sweeps"
                className="w-full px-3 py-2 text-sm bg-dark-bg border border-dark-border rounded text-white placeholder-gray-500 focus:outline-none focus:border-accent-yellow"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Symbol(s)</label>
              <TickerTagInput
                value={filters.symbol || ''}
                onChange={(value) => updateFilter('symbol', value)}
                placeholder="e.g., AAPL, NVDA, TSLA"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="space-y-3">
            <div className="text-xs text-gray-400 font-medium">Filter Criteria</div>

            {/* Row 2: Bias chips */}
            <ChipGroup<Bias>
              label="Bias"
              options={BIAS_OPTIONS}
              selected={filters.bias || []}
              onChange={(values) => updateFilter('bias', values)}
              colorMap={BIAS_COLORS}
            />

            {/* Row 3: Contract Type + Asset Class chips */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Contract Type</label>
                <select
                  value={filters.contract_type}
                  onChange={(e) => updateFilter('contract_type', e.target.value as ContractType | '')}
                  className="w-full px-2 py-1.5 text-xs bg-dark-bg border border-dark-border rounded text-white focus:outline-none focus:border-accent-yellow"
                >
                  <option value="">All</option>
                  <option value="Call">Call</option>
                  <option value="Put">Put</option>
                </select>
              </div>
              <ChipGroup<AssetClass>
                label="Asset Class"
                options={ASSET_CLASS_OPTIONS}
                selected={filters.asset_class || []}
                onChange={(values) => updateFilter('asset_class', values)}
              />
            </div>

            {/* Row 4: Sweeps Only + Opening Only */}
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={filters.sweeps_only || false}
                  onChange={(e) => updateFilter('sweeps_only', e.target.checked)}
                  className="rounded border-dark-border bg-dark-bg text-accent-yellow focus:ring-accent-yellow focus:ring-offset-0"
                />
                <span className="text-xs text-gray-400 group-hover:text-gray-300">Sweeps Only</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={filters.opening_only || false}
                  onChange={(e) => updateFilter('opening_only', e.target.checked)}
                  className="rounded border-dark-border bg-dark-bg text-accent-yellow focus:ring-accent-yellow focus:ring-offset-0"
                />
                <span className="text-xs text-gray-400 group-hover:text-gray-300">Opening Only</span>
              </label>
            </div>

            {/* Row 5: Top Bet + DTE range */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Top Bets Only</label>
                <select
                  value={filters.top_bet === true ? 'true' : ''}
                  onChange={(e) => updateFilter('top_bet', e.target.value === 'true' ? true : '')}
                  className="w-full px-2 py-1.5 text-xs bg-dark-bg border border-dark-border rounded text-white focus:outline-none focus:border-accent-yellow"
                >
                  <option value="">No</option>
                  <option value="true">Yes</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Min DTE</label>
                <input
                  type="number"
                  placeholder="e.g., 0"
                  value={filters.dte_min}
                  onChange={(e) => updateFilter('dte_min', e.target.value ? Number(e.target.value) : '')}
                  className="w-full px-2 py-1.5 text-xs bg-dark-bg border border-dark-border rounded text-white placeholder-gray-500 focus:outline-none focus:border-accent-yellow"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Max DTE</label>
                <input
                  type="number"
                  placeholder="e.g., 30"
                  value={filters.dte_max}
                  onChange={(e) => updateFilter('dte_max', e.target.value ? Number(e.target.value) : '')}
                  className="w-full px-2 py-1.5 text-xs bg-dark-bg border border-dark-border rounded text-white placeholder-gray-500 focus:outline-none focus:border-accent-yellow"
                />
              </div>
            </div>

            {/* Row 6: Premium Size (Bet Range) */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Min Premium ($)</label>
                <input
                  type="number"
                  placeholder="e.g., 100000"
                  value={filters.bet_min}
                  onChange={(e) => updateFilter('bet_min', e.target.value ? Number(e.target.value) : '')}
                  className="w-full px-2 py-1.5 text-xs bg-dark-bg border border-dark-border rounded text-white placeholder-gray-500 focus:outline-none focus:border-accent-yellow"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Max Premium ($)</label>
                <input
                  type="number"
                  placeholder="e.g., 1000000"
                  value={filters.bet_max}
                  onChange={(e) => updateFilter('bet_max', e.target.value ? Number(e.target.value) : '')}
                  className="w-full px-2 py-1.5 text-xs bg-dark-bg border border-dark-border rounded text-white placeholder-gray-500 focus:outline-none focus:border-accent-yellow"
                />
              </div>
            </div>

            {/* Row 7: Multiplier Range */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Min Multiplier</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="e.g., 2.0"
                  value={filters.multiplier_min}
                  onChange={(e) => updateFilter('multiplier_min', e.target.value ? Number(e.target.value) : '')}
                  className="w-full px-2 py-1.5 text-xs bg-dark-bg border border-dark-border rounded text-white placeholder-gray-500 focus:outline-none focus:border-accent-yellow"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Max Multiplier</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="e.g., 10.0"
                  value={filters.multiplier_max}
                  onChange={(e) => updateFilter('multiplier_max', e.target.value ? Number(e.target.value) : '')}
                  className="w-full px-2 py-1.5 text-xs bg-dark-bg border border-dark-border rounded text-white placeholder-gray-500 focus:outline-none focus:border-accent-yellow"
                />
              </div>
            </div>

            {/* Row 8: Baskets chips */}
            <ChipGroup<BasketTag>
              label="Baskets"
              options={BASKET_OPTIONS}
              selected={filters.baskets || []}
              onChange={(values) => updateFilter('baskets', values)}
            />

            {/* Row 9: Expiry Range */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Expiry From</label>
                <input
                  type="date"
                  value={filters.expiry_min}
                  onChange={(e) => updateFilter('expiry_min', e.target.value)}
                  className="w-full px-2 py-1.5 text-xs bg-dark-bg border border-dark-border rounded text-white focus:outline-none focus:border-accent-yellow"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Expiry To</label>
                <input
                  type="date"
                  value={filters.expiry_max}
                  onChange={(e) => updateFilter('expiry_max', e.target.value)}
                  className="w-full px-2 py-1.5 text-xs bg-dark-bg border border-dark-border rounded text-white focus:outline-none focus:border-accent-yellow"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-dark-border flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs text-gray-300 bg-dark-bg hover:bg-dark-surface-light border border-dark-border rounded transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!name.trim()}
            className="px-3 py-1.5 text-xs font-semibold text-dark-bg bg-accent-yellow hover:bg-accent-yellow/85 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {preset ? 'Save Changes' : 'Create Preset'}
          </button>
        </div>
      </div>
    </div>
  );
}
